import React, { useState, useEffect } from "react";
import { activateDemoPlan } from "../../../../Services/ClintsApi";
import { getPlane } from "../../../../Services/plansApi";
import {
  Box,
  Grid,
  TextField,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Card,
  CardContent,
  Divider,
  InputAdornment,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const ActivateDemoToPaid = ({
  tenant_id = "",
  defaultData = {},
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    end_date: defaultData.end_date || "",
    start_date: defaultData.start_date || "",
    plane_id: defaultData.plane_id || "",
    tenant_id: tenant_id || defaultData.tenant_id || "",
  });

  const [plans, setPlans] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // 🔹 جلب الخطط
  const fetchPlans = async () => {
    try {
      const res = await getPlane();
      const planes =
        res?.data?.data?.palnes || res?.data?.palnes || res?.data?.planes || [];
      setPlans(planes);
    } catch (err) {
      console.error("Failed to load plans:", err);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, tenant_id: tenant_id || "" }));
  }, [tenant_id]);

  const validate = () => {
    const err = {};
    if (!formData.start_date) err.start_date = "تاريخ البداية مطلوب";
    if (!formData.end_date) err.end_date = "تاريخ النهاية مطلوب";
    if (!formData.plane_id) err.plane_id = "يجب اختيار خطة";
    if (!formData.tenant_id) err.tenant_id = "رقم النسخة مطلوب";

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.end_date) <= new Date(formData.start_date)) {
        err.end_date = "تاريخ النهاية يجب أن يكون بعد البداية";
      }
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleActivate = async () => {
    if (!validate()) return;
    setLoading(true);
    setErrorMsg("");
    try {
      console.log("Activating demo plan with payload:", formData);
      await activateDemoPlan(formData);
      setSuccessMsg(
        "✅ تم تفعيل النسخة التجريبية بنجاح وتحويلها إلى نسخة مفعلة"
      );
      if (onSuccess) onSuccess();
      setTimeout(() => {
        if (onClose) onClose();
      }, 1000);
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.response?.data?.message || "حدث خطأ أثناء تفعيل النسخة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#e3f2fd",
            mb: 2,
          }}
        >
          <WorkspacePremiumIcon sx={{ fontSize: 32, color: "#1976d2" }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: "#1976d2", mb: 1 }}
        >
          تحويل النسخة التجريبية إلى نسخة مفعلة
        </Typography>
        <Typography variant="body2" color="text.secondary">
          قم بتعبئة البيانات التالية لتفعيل النسخة التجريبية وتحويلها إلى نسخة
          كاملة
        </Typography>
      </Box>

      {/* Alert Messages */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        {successMsg && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            {successMsg}
          </Alert>
        )}
        {errorMsg && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            {errorMsg}
          </Alert>
        )}

        {/* Warning Message */}
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{
            borderRadius: 2,
            backgroundColor: "#fff8e1",
            borderLeft: "4px solid #ff9800",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            تنبيه: بعد التفعيل، سيتم تحويل النسخة من تجريبية إلى نسخة كاملة مدفوعة
            ولا يمكن التراجع عن هذا الإجراء.
          </Typography>
        </Alert>
      </Stack>

      {/* Form Card */}
      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            {/* Dates Section */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1976d2",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CalendarTodayIcon fontSize="small" />
                فترة الاشتراك
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="تاريخ البداية"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                error={!!errors.start_date}
                helperText={errors.start_date}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="تاريخ النهاية"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                error={!!errors.end_date}
                helperText={errors.end_date}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
              />
            </Grid>

            {/* Divider */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Plan & Tenant Section */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#1976d2",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <WorkspacePremiumIcon fontSize="small" />
                تفاصيل الاشتراك
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="اختر الخطة"
                name="plane_id"
                value={formData.plane_id}
                onChange={handleChange}
                error={!!errors.plane_id}
                helperText={
                  errors.plane_id || "اختر الخطة التي سيتم تفعيل النسخة عليها"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkspacePremiumIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
              >
                {plans.length > 0 ? (
                  plans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.plane_name || plan.name || `خطة رقم ${plan.id}`}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>جارٍ تحميل الخطط...</MenuItem>
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="معرّف النسخة (tenant_id)"
                name="tenant_id"
                value={formData.tenant_id}
                onChange={handleChange}
                error={!!errors.tenant_id}
                helperText={
                  errors.tenant_id || "رقم النسخة التجريبية المطلوب تفعيلها"
                }
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 3 }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          size="large"
          sx={{
            px: 4,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          إلغاء
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleActivate}
          disabled={loading}
          size="large"
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />
          }
          sx={{
            px: 4,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: 3,
            "&:hover": {
              boxShadow: 6,
            },
          }}
        >
          {loading ? "جاري التفعيل..." : "تفعيل النسخة"}
        </Button>
      </Stack>
    </Box>
  );
};

export default ActivateDemoToPaid;
